<template>
  <!-- 문헌 수집 — 논문 상세 (Thermax LitPaperPage 대응): 탭 추출 데이터(버전·행 표·엑셀) / 원문(md, 근거 하이라이트) -->
  <div class="lit-page">
    <div class="title_cont">
      <h2 class="title_txt">문헌 수집</h2>
    </div>
    <div class="ptc_container" v-if="paper">
      <router-link to="/literature" class="lit-back">← 목록</router-link>
      <h3 class="lit-h">{{ paper.title }}</h3>
      <dl class="lit-meta">
        <dt>DOI</dt><dd><a :href="'https://doi.org/' + paper.doi" target="_blank" rel="noopener" class="mono">{{ paper.doi }}</a></dd>
        <dt>분야</dt><dd>{{ paper.group_label }}<span v-if="paper.groups && paper.groups.length > 1" class="lit-dim"> (+ {{ paper.groups.filter(c => c !== paper.group).join(', ') }})</span></dd>
        <dt>상태</dt><dd>{{ paper.status_label }}</dd>
        <dt>등록</dt><dd>{{ paper.uploader }}<span v-if="paper.mine" class="lit-me">(나)</span> · {{ paper.institution }} · {{ fmtDate(paper.created_at) }} · {{ paper.via }}</dd>
      </dl>

      <div class="lit-tabs">
        <button type="button" class="lit-tab" :class="{ on: tab === 'rows' }" @click="setTab('rows')">
          추출 데이터 <small v-if="uploads.length">({{ uploads.length }}버전)</small>
        </button>
        <button type="button" class="lit-tab" :class="{ on: tab === 'md' }" @click="setTab('md')">
          원문 (md) <small v-if="fulltexts.length">({{ fulltexts.length }}버전)</small>
        </button>
      </div>

      <!-- ===================== 추출 데이터 ===================== -->
      <div v-if="tab === 'rows'" class="lit-panel">
        <div v-if="!uploads.length" class="lit-empty">
          <p class="lit-dim">아직 올라온 추출 데이터가 없습니다.</p>
          <p class="lit-note">업로드: <code>python lit_push.py papers/&lt;논문ID&gt;/extraction.json</code> (팀 패키지 · 개인 업로드 토큰 필요)</p>
        </div>
        <template v-else>
          <div class="lit-verbar">
            <label>버전
              <select v-model.number="selectedUploadId" @change="loadRows(1)">
                <option v-for="u in uploads" :key="u.id" :value="u.id">
                  v{{ u.version }}{{ u.is_current ? ' (현재)' : '' }} · {{ u.row_count }}행 · {{ u.uploader }} · {{ fmtDate(u.created_at) }}
                </option>
              </select>
            </label>
            <div class="lit-chips" v-if="selectedUpload">
              <span v-for="(v, k) in selectedUpload.stats" :key="k" class="lit-chip small static">{{ statLabel(k) }} <b>{{ v }}</b></span>
              <button v-if="selectedUpload.warning_count" type="button" class="lit-chip small" :class="{ on: showWarnings }" @click="showWarnings = !showWarnings">
                경고 <b>{{ selectedUpload.warning_count }}</b>
              </button>
            </div>
            <div class="lit-verbar-right">
              <button type="button" class="sm_btn" :disabled="exporting" @click="exportRows">{{ exporting ? '내보내는 중…' : '엑셀' }}</button>
              <button v-if="selectedUpload && selectedUpload.can_delete" type="button" class="sm_btn danger" :disabled="busy" @click="deleteUpload">이 버전 삭제</button>
            </div>
          </div>
          <div class="lit-upmeta" v-if="selectedUpload">
            추출: {{ selectedUpload.extracted_by || '—' }} · {{ selectedUpload.extracted_at || '—' }} · {{ selectedUpload.tool || '—' }} · 원문 형식 {{ selectedUpload.source_format || '—' }} · 업로드 {{ selectedUpload.via }}
            <span v-if="selectedUpload.mine" class="lit-me">(나)</span>
          </div>
          <ul class="lit-warnings" v-if="showWarnings && selectedUpload">
            <li v-for="(w, i) in selectedUpload.warnings" :key="i"><span v-if="w.row_no">#{{ w.row_no }} </span><b>{{ w.field }}</b> {{ w.message }}</li>
          </ul>

          <div class="lit-toolbar">
            <div class="lit-search">
              <input type="text" v-model="propertyQ" placeholder="물성명 검색 (mobility, capacity …)" @keyup.enter="loadRows(1)">
              <button type="button" class="sm_btn" @click="loadRows(1)">검색</button>
            </div>
            <label class="lit-check"><input type="checkbox" v-model="reviewOnly" @change="loadRows(1)"> 검토필요만</label>
            <span class="lit-dim" v-if="rowList">{{ rowList.total.toLocaleString() }}행</span>
          </div>

          <div class="lit-table-wrap" v-if="rowList && rowList.items.length">
            <table class="lit-table lit-rows">
              <thead>
              <tr>
                <th>#</th><th>시편</th><th>물성</th><th>값</th><th>단위</th><th>성격</th><th>측정법</th><th>위치</th><th>원문 문장</th><th>표시</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="r in rowList.items" :key="r.id" :class="{ review: r.needs_review }">
                <td class="mono">{{ r.row_no }}</td>
                <td>
                  <span v-if="r.sample_no != null">#{{ r.sample_no }}</span>
                  <div class="lit-sub" v-if="r.sample_type">{{ r.sample_type }}</div>
                  <div class="lit-sub" v-if="sampleLabel(r)">{{ sampleLabel(r) }}</div>
                </td>
                <td>{{ r.property_name }}<div class="lit-sub" v-if="r.property_detail">{{ r.property_detail }}</div></td>
                <td class="mono num">{{ r.value == null ? '—' : r.value }}</td>
                <td>{{ r.unit || '' }}</td>
                <td><span class="lit-sub">{{ r.value_qualifier || '' }}</span></td>
                <td>{{ (r.data && (r.data.method || r.data.measurement_method)) || '' }}</td>
                <td class="lit-sub">{{ r.source_section || '' }}</td>
                <td class="quote">
                  <span>{{ r.evidence_quote || '' }}</span>
                  <a v-if="r.evidence_quote && currentFulltext" href="#" class="lit-find" title="원문(md)에서 이 문장 찾기" @click.prevent="findInMd(r.evidence_quote)">원문에서 찾기</a>
                </td>
                <td class="flags">
                  <span v-if="r.needs_review" class="lit-flag warn" title="종합 검토 필요">검토</span>
                  <span v-if="r.needs_graph_check" class="lit-flag" title="그래프 확인 필요">그래프</span>
                  <span v-if="r.is_citation" class="lit-flag" title="타 문헌 인용값">인용</span>
                  <span v-if="r.is_model_prediction" class="lit-flag" title="모델 예측값">모델</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="search_nodata" v-else-if="rowList && !loading"><h3>조건에 맞는 행이 없습니다.</h3></div>
          <div class="lit-paging" v-if="rowList && rowPages > 1">
            <button type="button" class="sm_btn" :disabled="rowPage <= 1" @click="loadRows(rowPage - 1)">이전</button>
            <span>{{ rowPage }} / {{ rowPages }}</span>
            <button type="button" class="sm_btn" :disabled="rowPage >= rowPages" @click="loadRows(rowPage + 1)">다음</button>
          </div>
        </template>
      </div>

      <!-- ===================== 원문 (md) ===================== -->
      <div v-else class="lit-panel">
        <div v-if="!fulltexts.length" class="lit-empty">
          <p class="lit-dim">아직 올라온 원문(md)이 없습니다.</p>
          <p class="lit-note">업로드: <code>python lit_push.py --md papers/&lt;논문ID&gt;/paper.md</code></p>
        </div>
        <template v-else>
          <div class="lit-verbar">
            <label>버전
              <select v-model.number="selectedFulltextId" @change="loadFulltext">
                <option v-for="f in fulltexts" :key="f.id" :value="f.id">
                  v{{ f.version }}{{ f.is_current ? ' (현재)' : '' }} · {{ Math.round(f.byte_size / 1024) }} KB · {{ f.uploader }} · {{ fmtDate(f.created_at) }}{{ f.can_read ? '' : ' · 열람 범위 밖' }}
                </option>
              </select>
            </label>
            <label v-if="currentFulltext && currentFulltext.can_manage">열람 범위
              <select :value="currentFulltext.share_scope" :disabled="busy" @change="changeScope($event.target.value)">
                <option value="uploader">올린 사람만</option>
                <option value="institution">같은 기관</option>
                <option value="all">로그인 전원</option>
              </select>
            </label>
            <span v-else-if="currentFulltext" class="lit-dim">열람 범위: {{ scopeLabel(currentFulltext.share_scope) }}</span>
            <div class="lit-verbar-right">
              <button v-if="currentFulltext && currentFulltext.can_manage" type="button" class="sm_btn danger" :disabled="busy" @click="deleteFulltext">이 버전 삭제</button>
            </div>
          </div>
          <div class="lit-upmeta" v-if="currentFulltext">
            원문 형식 {{ currentFulltext.source_format || '—' }} · 변환 {{ currentFulltext.converter || '—' }} · {{ currentFulltext.char_count.toLocaleString() }}자
            <span v-if="currentFulltext.warning_count"> · 경고 {{ currentFulltext.warning_count }}건: {{ currentFulltext.warnings.map(w => w.message).join(' / ') }}</span>
          </div>
          <div v-if="highlight" class="lit-hlbar">
            근거 문장 찾기: <em>{{ highlight.slice(0, 80) }}{{ highlight.length > 80 ? '…' : '' }}</em>
            <span v-if="highlightFound === false" class="lit-dim"> — 원문에서 찾지 못했습니다(표기 차이). 검색(Ctrl+F)으로 확인하세요.</span>
            <a href="#" @click.prevent="highlight = ''">해제</a>
          </div>
          <div v-if="ftDenied" class="search_nodata"><h3>이 버전은 열람 범위 밖입니다.</h3><p>올린 팀에 문의하거나 다른 버전을 선택하세요.</p></div>
          <LitMarkdown v-else-if="ftContent != null" :source="ftContent" :highlight="highlight" @highlight-result="highlightFound = $event" />
        </template>
      </div>
    </div>
    <div class="ptc_container" v-else-if="error"><p class="lit-error">{{ error }}</p></div>
    <Loading :visible="loading" />
  </div>
</template>

<script>
import Loading from "@/components/common/loading.vue";
import LitMarkdown from "@/components/literature/LitMarkdown.vue";

const ROWS_PAGE = 200;
const STAT_LABELS = {
  rows: "행", samples: "시편", properties: "물성 수", needs_review: "검토필요", needs_graph_check: "그래프확인",
  citations: "인용값", model_predictions: "모델값", mobility_rows: "이동도 행", capacity_rows: "용량 행", tc_rows: "열전도도 행", key_rows: "핵심 행",
};

export default {
  name: "LitPaper",
  components: { Loading, LitMarkdown },
  data() {
    return {
      loading: false, busy: false, exporting: false, error: null,
      paper: null,
      uploads: [], selectedUploadId: null, rowList: null, rowPage: 1, propertyQ: "", reviewOnly: false, showWarnings: false,
      fulltexts: [], selectedFulltextId: null, ftContent: null, ftDenied: false,
      highlight: "", highlightFound: null,
    };
  },
  computed: {
    id() { return this.$route.params.id; },
    tab() { return this.$route.query.tab === "md" ? "md" : "rows"; },
    selectedUpload() { return this.uploads.find((u) => u.id === this.selectedUploadId) || null; },
    currentFulltext() { return this.fulltexts.find((f) => f.id === this.selectedFulltextId) || null; },
    rowPages() { return this.rowList ? Math.max(1, Math.ceil(this.rowList.total / ROWS_PAGE)) : 1; },
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      const [paper, uploads, fulltexts] = await Promise.all([
        this.$wApi.lit.paper({}, this.id), this.$wApi.lit.uploads({}, this.id), this.$wApi.lit.fulltexts({}, this.id),
      ]);
      this.loading = false;
      if (!paper || paper.error) { this.error = (paper && paper.message) || "논문을 불러오지 못했습니다."; return; }
      this.paper = paper;
      this.uploads = Array.isArray(uploads) ? uploads : [];
      this.fulltexts = Array.isArray(fulltexts) ? fulltexts : [];
      const cur = this.uploads.find((u) => u.is_current) || this.uploads[0];
      this.selectedUploadId = cur ? cur.id : null;
      if (cur) await this.loadRows(1);
      const ft = this.fulltexts.find((f) => f.is_current) || this.fulltexts[0];
      this.selectedFulltextId = ft ? ft.id : null;
      if (this.tab === "md" && ft) await this.loadFulltext();
    },
    setTab(t) {
      if (t === this.tab) return;
      this.$router.replace({ path: this.$route.path, query: t === "md" ? { tab: "md" } : {} });
      if (t === "md" && this.currentFulltext && this.ftContent == null) this.loadFulltext();
    },
    async loadRows(page) {
      if (!this.selectedUploadId) return;
      this.loading = true;
      this.rowPage = page || 1;
      const res = await this.$wApi.lit.rows({ page: this.rowPage, pageSize: ROWS_PAGE, property: this.propertyQ.trim(), needsReview: this.reviewOnly ? "1" : "0" }, this.selectedUploadId);
      this.loading = false;
      if (!res || res.error) { this.error = (res && res.message) || "행을 불러오지 못했습니다."; return; }
      this.rowList = res;
    },
    async loadFulltext() {
      if (!this.selectedFulltextId) return;
      this.ftContent = null; this.ftDenied = false; this.highlightFound = null;
      const f = this.currentFulltext;
      if (f && !f.can_read) { this.ftDenied = true; return; }
      this.loading = true;
      const res = await this.$wApi.lit.fulltext({}, this.selectedFulltextId);
      this.loading = false;
      if (!res || res.error) { this.ftDenied = true; return; }
      this.ftContent = res.content || "";
    },
    findInMd(quote) {
      this.highlight = quote;
      this.highlightFound = null;
      if (this.tab !== "md") this.setTab("md");
      else if (this.ftContent == null) this.loadFulltext();
    },
    async changeScope(scope) {
      if (!this.currentFulltext || scope === this.currentFulltext.share_scope) return;
      this.busy = true;
      const res = await this.$wApi.lit.patchFulltext({ share_scope: scope }, this.currentFulltext.id);
      this.busy = false;
      if (!res || res.error) { window.alert((res && res.message) || "열람 범위를 바꾸지 못했습니다."); return; }
      const idx = this.fulltexts.findIndex((f) => f.id === res.id);
      if (idx >= 0) this.$set(this.fulltexts, idx, res);
    },
    async deleteFulltext() {
      const f = this.currentFulltext;
      if (!f || !window.confirm(`원문 v${f.version} 을 삭제하시겠습니까? 현재 버전이면 직전 버전이 현재로 돌아옵니다.`)) return;
      this.busy = true;
      const res = await this.$wApi.lit.deleteFulltext({}, f.id);
      this.busy = false;
      if (res && res.error) { window.alert(res.message || "삭제하지 못했습니다."); return; }
      await this.loadAll();
    },
    async deleteUpload() {
      const u = this.selectedUpload;
      if (!u || !window.confirm(`추출 데이터 v${u.version} (${u.row_count}행) 을 삭제하시겠습니까? 현재 버전이면 직전 버전이 현재로 돌아옵니다.`)) return;
      this.busy = true;
      const res = await this.$wApi.lit.deleteUpload({}, u.id);
      this.busy = false;
      if (res && res.error) { window.alert(res.message || "삭제하지 못했습니다."); return; }
      await this.loadAll();
    },
    async exportRows() {
      if (!this.selectedUpload) return;
      this.exporting = true;
      try {
        const all = [];
        let page = 1;
        for (;;) {
          const res = await this.$wApi.lit.rows({ page, pageSize: 2000, property: "", needsReview: "0" }, this.selectedUploadId);
          if (!res || res.error || !res.items.length) break;
          all.push(...res.items);
          if (all.length >= res.total) break;
          page += 1;
        }
        const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet(`v${this.selectedUpload.version}`);
        // 원본 열(data) 을 그대로 내보낸다 — 열 순서는 첫 행 기준
        const keys = [];
        all.forEach((r) => Object.keys(r.data || {}).forEach((k) => { if (!keys.includes(k)) keys.push(k); }));
        ws.columns = [{ header: "row_no", key: "row_no", width: 8 }, ...keys.map((k) => ({ header: k, key: k, width: Math.min(40, Math.max(10, k.length + 2)) }))];
        all.forEach((r) => ws.addRow({ row_no: r.row_no, ...(r.data || {}) }));
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${this.paper.doi.replace(/[\\/:*?"<>|]/g, "_")}_v${this.selectedUpload.version}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (e) {
        window.alert("엑셀 내보내기에 실패했습니다: " + (e && e.message ? e.message : e));
      } finally {
        this.exporting = false;
      }
    },
    sampleLabel(r) {
      const d = r.data || {};
      return d.material_name || d.chemical_formula || d.filler_chemistry || "";
    },
    statLabel(k) { return STAT_LABELS[k] || k; },
    scopeLabel(s) { return { uploader: "올린 사람만", institution: "같은 기관", all: "로그인 전원" }[s] || s; },
    fmtDate(iso) { return iso ? String(iso).slice(0, 10) : ""; },
  },
};
</script>

<style scoped>
.lit-back { display: inline-block; margin-bottom: 10px; color: #0c78b3; font-size: 13px; }
.lit-h { font-size: 20px; font-weight: 600; color: #223; margin: 0 0 12px; line-height: 1.4; }
.lit-meta { display: grid; grid-template-columns: 70px 1fr; gap: 6px 10px; font-size: 13px; margin-bottom: 18px; }
.lit-meta dt { color: #778; }
.lit-meta dd { margin: 0; color: #333; }
.mono { font-family: Consolas, "Courier New", monospace; font-size: 12px; }
.lit-meta .mono { color: #0c78b3; }
.num { text-align: right; white-space: nowrap; }
.lit-dim { color: #99a; }
.lit-me { margin-left: 4px; font-size: 11px; color: #0c78b3; }
.lit-tabs { display: flex; gap: 4px; border-bottom: 1px solid #dde3ea; margin-bottom: 14px; }
.lit-tab { border: none; background: none; padding: 8px 14px; font-size: 14px; color: #667; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.lit-tab.on { color: #003f67; border-bottom-color: #003f67; font-weight: 600; }
.lit-tab small { color: #99a; font-weight: 400; }
.lit-panel { font-size: 13px; color: #334; line-height: 1.6; }
.lit-empty { padding: 20px 0; }
.lit-note { color: #667; }
.lit-note code, .lit-hlbar em { background: #f4f7fa; padding: 1px 5px; border-radius: 3px; }
.lit-verbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 8px; }
.lit-verbar label { display: flex; gap: 6px; align-items: center; color: #556; }
.lit-verbar select { height: 30px; border: 1px solid #cfd8e3; border-radius: 4px; max-width: 480px; font-size: 12.5px; }
.lit-verbar-right { margin-left: auto; display: flex; gap: 6px; }
.sm_btn.danger { border-color: #c44; color: #c44; }
.lit-upmeta { font-size: 12px; color: #667; margin-bottom: 10px; }
.lit-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.lit-chip { border: 1px solid #cfd8e3; background: #fff; border-radius: 999px; padding: 4px 10px; font-size: 12px; color: #334; cursor: pointer; }
.lit-chip.static { cursor: default; }
.lit-chip b { margin-left: 3px; color: #003f67; }
.lit-chip.on { background: #003f67; border-color: #003f67; color: #fff; }
.lit-chip.on b { color: #fff; }
.lit-warnings { background: #fff8e6; border: 1px solid #f0c36d; border-radius: 6px; padding: 8px 14px 8px 28px; margin: 0 0 12px; font-size: 12px; color: #7a5200; max-height: 180px; overflow-y: auto; }
.lit-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin: 10px 0; }
.lit-search { display: flex; gap: 6px; }
.lit-search input { width: 280px; height: 32px; border: 1px solid #cfd8e3; border-radius: 4px; padding: 0 10px; font-size: 13px; }
.lit-check { display: flex; align-items: center; gap: 4px; color: #445; }
.lit-table-wrap { overflow-x: auto; border-top: 2px solid #003f67; }
.lit-table { width: 100%; border-collapse: collapse; font-size: 12.5px; min-width: 1100px; }
.lit-table th { background: #f4f7fa; padding: 8px 7px; text-align: left; border-bottom: 1px solid #dde3ea; font-weight: 600; color: #223; white-space: nowrap; }
.lit-table td { padding: 7px; border-bottom: 1px solid #eef1f5; vertical-align: top; color: #333; }
.lit-rows tr.review td { background: #fffbf0; }
.lit-sub { font-size: 11px; color: #778; }
.quote { max-width: 420px; color: #445; }
.lit-find { display: block; margin-top: 3px; font-size: 11px; color: #0c78b3; }
.flags { white-space: nowrap; }
.lit-flag { display: inline-block; padding: 1px 6px; border-radius: 999px; font-size: 10.5px; background: #eef1f5; color: #556; margin-right: 3px; }
.lit-flag.warn { background: #fff0c2; color: #7a5200; }
.lit-paging { display: flex; gap: 12px; align-items: center; justify-content: center; margin: 16px 0; color: #556; }
.lit-hlbar { background: #fff8e6; border: 1px solid #f0c36d; border-radius: 6px; padding: 6px 12px; margin-bottom: 12px; font-size: 12.5px; color: #7a5200; }
.lit-hlbar a { margin-left: 8px; color: #7a5200; text-decoration: underline; }
.lit-error { color: #b23; }
.search_nodata p { color: #667; font-size: 13px; margin-top: 8px; }
</style>
